import * as Api from '@ViewModels';
import { UseFormGetValues, UseFormReset } from 'react-hook-form';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import {
	invalidateInfiniteSurveys,
	invalidateSurveyById,
	useCreateCustomSurveyMutation,
	useReorderCustomSurveyFieldsMutation,
	useUpdateCustomSurveyMutation,
} from '../../../../../queries';
import {
	invalidateSystemSurveyTemplateQuery,
	useReorderSurveyTemplateFieldsMutation,
} from '../../../../../queries/SurveyTemplate';
import { CustomSurveyFormData } from '../utils';

interface UseCustomSurveyMutationsProps {
	impersonationContext?: Api.IImpersonationContext;
	getValues: UseFormGetValues<CustomSurveyFormData>;
	reset: UseFormReset<CustomSurveyFormData>;
	surveyData?: Api.ICustomSurvey;
	onReorderSuccess?: (fields: Api.IFormField<string>[]) => void;
	/** When true, uses admin template reorder mutation instead of survey reorder mutation */
	templateMode?: boolean;
	/** Template ID when in template mode - required for invalidating template queries */
	templateId?: string;
}

// Hook for mutations
export const useCustomSurveyMutations = ({
	impersonationContext,
	getValues,
	reset,
	onReorderSuccess,
	templateMode = false,
	templateId,
}: UseCustomSurveyMutationsProps) => {
	const history = useHistory();
	const { logApiError } = useEventLogging('CustomSurvey');
	const errorMessages = useErrorMessages();

	// Step 1: Update existing survey
	const updateSurveyMutation = useUpdateCustomSurveyMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update the custom survey');
			logApiError('CustomUpdateSurveySave-Error', error);
		},
		onSuccess: () => {
			toast.success('Custom survey updated successfully');
			invalidateSurveyById();
			invalidateInfiniteSurveys();
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
		},
	});

	// Step 1: Create new survey
	const createSurveyMutation = useCreateCustomSurveyMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create custom survey');
			logApiError('CustomCreateSurvey-Error', error);
		},
		onSuccess: (val: Api.ICustomSurvey) => {
			toast.success('Custom survey created');
			history.replace(`/surveys/custom/edit/${val.id}`);
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
		},
	});

	// Reorder survey fields - regular survey mode
	const reorderSurveyFieldsMutation = useReorderCustomSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder custom survey fields');
			logApiError('CustomReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Custom survey fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
		},
	});

	// Reorder template fields - template mode
	const reorderTemplateFieldsMutation = useReorderSurveyTemplateFieldsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder template fields');
			logApiError('TemplateReorderFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Template fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
			// Invalidate template query to refetch with new field order
			if (templateId) {
				invalidateSystemSurveyTemplateQuery(templateId);
			}
		},
	});

	return {
		updateSurveyMutation,
		createSurveyMutation,
		reorderSurveyFieldsMutation,
		reorderTemplateFieldsMutation,
		templateMode,
	};
};
